import React, { useEffect, useState, useMemo } from "react";
import classes from "./AdminExpenseUnitsPage.module.css";
import { getExpenseUnits, upsertExpenseUnit } from "../../../scripts/dataHandlers";
import { ExpenseUnit } from "../../../contexts/interfaces";
import ReactLoading from "react-loading";
import { useErrorContext } from "../../../contexts/ErrorContext";
import axios from "axios";
import { FaEdit, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { BsPlusCircle } from "react-icons/bs";
import EditExpenseUnitModal from "./EditExpenseUnitModal";
import { toast } from "react-toastify";

type SortColumn = "id" | "name" | "createdAt";
type SortDirection = "asc" | "desc";

const AdminExpenseUnitsPage = () => {
  const [expenseUnits, setExpenseUnits] = useState<ExpenseUnit[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sortColumn, setSortColumn] = useState<SortColumn>("id");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [editingUnit, setEditingUnit] = useState<ExpenseUnit | null>(null);
  const [isAddMode, setIsAddMode] = useState<boolean>(false);
  const [editFormData, setEditFormData] = useState<{ id: number; name: string }>({ id: 0, name: "" });
  const [validationErrors, setValidationErrors] = useState<{ id?: string; name?: string }>({});
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [searchFilter, setSearchFilter] = useState<string>("");
  const { handleError } = useErrorContext();

  useEffect(() => {
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();

    const fetchExpenseUnits = async () => {
      setIsLoading(true);
      const res = await getExpenseUnits(source);
      
      if (res?.status === 200) {
        setExpenseUnits(res.data.result || []);
      } else {
        handleError(res, "GetExpenseUnitsFailed");
      }
      setIsLoading(false);
    };

    fetchExpenseUnits();

    return () => source.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("fi-FI", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const formatDateTime = (dateString: string | null): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("fi-FI", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const toggleRowExpansion = (unitId: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(unitId)) {
        newSet.delete(unitId);
      } else {
        newSet.add(unitId);
      }
      return newSet;
    });
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedExpenseUnits = useMemo(() => {
    // First filter by search term
    let filtered = expenseUnits;
    if (searchFilter.trim() !== "") {
      const filterLower = searchFilter.toLowerCase().trim();
      filtered = expenseUnits.filter((unit) => {
        // Check if search term matches ID (must start with the search term)
        const idMatch = unit.id.toString().startsWith(filterLower);
        // Check if search term matches name (case-insensitive, can be anywhere in name)
        const nameMatch = unit.name.toLowerCase().includes(filterLower);
        return idMatch || nameMatch;
      });
    }

    // Then sort the filtered results
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case "id":
          aValue = a.id;
          bValue = b.id;
          break;
        case "name":
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case "createdAt":
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === "asc" ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [expenseUnits, sortColumn, sortDirection, searchFilter]);

  const handleEditClick = (unit: ExpenseUnit) => {
    setEditingUnit(unit);
    setIsAddMode(false);
    setEditFormData({ id: unit.id, name: unit.name });
    setValidationErrors({});
  };

  const handleAddClick = () => {
    setEditingUnit(null);
    setIsAddMode(true);
    setEditFormData({ id: 0, name: "" });
    setValidationErrors({});
  };

  const handleCloseModal = () => {
    setEditingUnit(null);
    setIsAddMode(false);
    setEditFormData({ id: 0, name: "" });
    setValidationErrors({});
  };

  const validateForm = (): boolean => {
    const errors: { id?: string; name?: string } = {};

    // Validate ID only when adding new (not in edit mode)
    if (isAddMode) {
      if (!editFormData.id || editFormData.id <= 0) {
        errors.id = "Kustannuspaikka on pakollinen ja sen on oltava positiivinen luku";
      } else if (isNaN(editFormData.id)) {
        errors.id = "Kustannuspaikan on oltava numero";
      } else if (expenseUnits.some((unit) => unit.id === editFormData.id)) {
        errors.id = "Tämä kustannuspaikka on jo olemassa";
      }
    }

    // Validate Name
    if (!editFormData.name || editFormData.name.trim() === "") {
      errors.name = "Nimi on pakollinen";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    // Show loading toast
    toast("Tallennetaan...", {
      toastId: "upsertExpenseUnit",
      position: "top-center",
      autoClose: false,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
      type: "default",
    });

    try {
      const res = await upsertExpenseUnit(editFormData.id, editFormData.name.trim());
      toast.dismiss("upsertExpenseUnit");

      if (res?.status === 200 && res?.data?.result) {
        const updatedUnit = res.data.result;
        
        // Show success message
        toast(
          isAddMode
            ? "Kustannuspaikka lisätty onnistuneesti"
            : "Kustannuspaikka päivitetty onnistuneesti",
          {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            type: "success",
          }
        );

        // Update local state with the returned data
        if (isAddMode) {
          // Add new unit with foremen and formHandlers from the response
          const newUnit: ExpenseUnit = {
            id: updatedUnit.id,
            name: updatedUnit.name,
            createdAt: updatedUnit.createdAt,
            updatedAt: updatedUnit.updatedAt,
            foremen: updatedUnit.foremen || [],
            formHandlers: updatedUnit.formHandlers || [],
          };
          setExpenseUnits((prev) => [...prev, newUnit]);
        } else if (editingUnit) {
          // Update existing unit with foremen and formHandlers from the response
          const editingUnitId = editingUnit.id;
          setExpenseUnits((prev) =>
            prev.map((unit) =>
              unit.id === editingUnitId
                ? {
                    ...unit,
                    name: updatedUnit.name,
                    updatedAt: updatedUnit.updatedAt,
                    foremen: updatedUnit.foremen || [],
                    formHandlers: updatedUnit.formHandlers || [],
                  }
                : unit
            )
          );
        }

        handleCloseModal();
      } else {
        handleError(res, "UpsertExpenseUnitFailed");
      }
    } catch (err: any) {
      toast.dismiss("upsertExpenseUnit");
      handleError(err, "UpsertExpenseUnitFailed");
    }
  };

  const handleInputChange = (field: "id" | "name", value: string | number) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: field === "id" ? Number(value) : value,
    }));
    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <ReactLoading
          type={"spokes"}
          color={"#ED1B24"}
          height={"50px"}
          width={"50px"}
        />
        <div className={classes.loadingText}>Ladataan...</div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.headerRow}>
        <h2 className={classes.title}>Kustannuspaikat</h2>
        <button className={classes.addButton} onClick={handleAddClick}>
          <BsPlusCircle className={classes.addIcon} />
          Lisää uusi
        </button>
      </div>
      <div className={classes.searchContainer}>
        <input
          type="text"
          className={classes.searchInput}
          placeholder="Hae kustannuspaikalla tai nimellä..."
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
        />
      </div>
      {sortedExpenseUnits.length > 0 ? (
        <table className={classes.table}>
          <thead>
            <tr>
              <th
                className={classes.sortableHeader}
                onClick={() => handleSort("id")}
              >
                Kustannuspaikka
                {sortColumn === "id" && (
                  <span className={classes.sortIndicator}>
                    {sortDirection === "asc" ? " ↑" : " ↓"}
                  </span>
                )}
              </th>
              <th
                className={classes.sortableHeader}
                onClick={() => handleSort("name")}
              >
                Nimi
                {sortColumn === "name" && (
                  <span className={classes.sortIndicator}>
                    {sortDirection === "asc" ? " ↑" : " ↓"}
                  </span>
                )}
              </th>
              <th
                className={classes.sortableHeader}
                onClick={() => handleSort("createdAt")}
              >
                Luotu
                {sortColumn === "createdAt" && (
                  <span className={classes.sortIndicator}>
                    {sortDirection === "asc" ? " ↑" : " ↓"}
                  </span>
                )}
              </th>
              <th>Toiminnot</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedExpenseUnits.map((unit) => {
              const isExpanded = expandedRows.has(unit.id);
              return (
                <React.Fragment key={unit.id}>
                  <tr 
                    className={classes.tableRow}
                    onClick={() => toggleRowExpansion(unit.id)}
                  >
                    <td>{unit.id}</td>
                    <td>{unit.name}</td>
                    <td>{formatDate(unit.createdAt)}</td>
                    <td>
                      <button
                        className={classes.editButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(unit);
                        }}
                      >
                        <FaEdit className={classes.editIcon} />
                      </button>
                    </td>
                    <td>
                      <button
                        className={classes.expandButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRowExpansion(unit.id);
                        }}
                      >
                        {isExpanded ? (
                          <FaChevronUp className={classes.expandIcon} />
                        ) : (
                          <FaChevronDown className={classes.expandIcon} />
                        )}
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className={classes.expandedRow}>
                      <td colSpan={5} className={classes.expandedCell}>
                        <div className={classes.expandedContent}>
                          <div className={classes.expandedSection}>
                            <div className={classes.expandedLabel}>Luotu:</div>
                            <div className={classes.expandedValue}>
                              {formatDateTime(unit.createdAt)}
                            </div>
                          </div>
                          <div className={classes.expandedSection}>
                            <div className={classes.expandedLabel}>Päivitetty:</div>
                            <div className={classes.expandedValue}>
                              {formatDateTime(unit.updatedAt)}
                            </div>
                          </div>
                          <div className={classes.expandedSection}>
                            <div className={classes.expandedLabel}>Esihenkilöt:</div>
                            <div className={classes.expandedValue}>
                              {unit.foremen && unit.foremen.length > 0 ? (
                                <ul className={classes.employeeList}>
                                  {unit.foremen.map((foreman) => (
                                    <li key={`foreman-${foreman.id}`}>
                                      {foreman.firstName} {foreman.lastName}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <span className={classes.noEmployees}>Ei esihenkilöitä</span>
                              )}
                            </div>
                          </div>
                          <div className={classes.expandedSection}>
                            <div className={classes.expandedLabel}>Lääkelupavastaavat:</div>
                            <div className={classes.expandedValue}>
                              {unit.formHandlers && unit.formHandlers.length > 0 ? (
                                <ul className={classes.employeeList}>
                                  {unit.formHandlers.map((formHandler) => (
                                    <li key={`formHandler-${formHandler.id}`}>
                                      {formHandler.firstName} {formHandler.lastName}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <span className={classes.noEmployees}>Ei lääkelupavastaavia</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className={classes.noResultsText}>
          {searchFilter.trim() !== ""
            ? "Hakutuloksia ei löytynyt."
            : "Kustannuspaikkoja ei löytynyt."}
        </div>
      )}

      <EditExpenseUnitModal
        isOpen={!!editingUnit || isAddMode}
        isEditMode={!!editingUnit}
        formData={editFormData}
        onClose={handleCloseModal}
        onSave={handleSave}
        onInputChange={handleInputChange}
        validationErrors={validationErrors}
      />
    </div>
  );
};

export default AdminExpenseUnitsPage;
